import { useRef } from 'react'
import toast from 'react-hot-toast'

import { useFvNavigate } from '@fv/client-components'
import type { Workflow } from '@fv/models'

import { Loading } from '@/components/Loading'
import { useQueryParam } from '@/hooks/settings'
import { useLoad } from '@/hooks/shipments'
import { useQuoteRequestTemplate } from '@/hooks/shipments/useRepeatShipment'
import { useLoadRatesPage } from '@/route-hooks'
import { routes } from '@/routes'

import { usePermissions } from '../auth'
import { useRole } from '../auth/hooks'
import { ShipmentTemplateSelector } from '../load-templates/ShipmentTemplateSelector'
import { useDefaultWorkflow } from '../profiile/hooks'
import { useUnifiedShipmentFlow } from '../route-guides/hooks'
import { useCreateShipment } from './mutations'
import { ShipmentForm, type ShipmentFormHandle } from './ShipmentForm'
import { ShipmentFormPageLayout } from './ShipmentFormPageLayout'
import { ShipmentFormProvider } from './ShipmentFormProvider'
import { useShipmentFormActions, useShipmentFormState } from './store'
import type { ShipmentFormModel } from './types'
import { mapShipmentFormModelToLoadDTO } from './utils'
import { WorkflowSelector } from './WorkflowSelector'

export const ShipmentCreatePage = () => {
  const defaultWorkflow = useDefaultWorkflow()
  const unifiedFlow = useUnifiedShipmentFlow()
  const copyFrom = useQueryParam('copyFrom')
  const copyQuery = useLoad(copyFrom)
  const [quoteRequestTemplate] = useQuoteRequestTemplate()
  // "Start over" discards the source before landing here, so fall back to the snapshot taken before it was discarded.
  const copySource =
    copyQuery.data ??
    (copyQuery.isError
      ? quoteRequestTemplate?.find(l => l.loadId === copyFrom)
      : undefined)

  if (unifiedFlow.isLoading || (copyFrom && copyQuery.isLoading)) {
    return <Loading />
  }

  const workflow =
    unifiedFlow.isEnabled('truckload') &&
    !unifiedFlow.isEnabled(defaultWorkflow)
      ? 'truckload'
      : defaultWorkflow

  return (
    <ShipmentFormProvider
      key={copyFrom ?? 'new'}
      init={copySource ? { copyFrom: copySource } : { workflow }}
    >
      <ShipmentCreatePageContent isCopy={!!copyFrom} />
    </ShipmentFormProvider>
  )
}

const ShipmentCreatePageContent = ({ isCopy }: { isCopy: boolean }) => {
  const { workflow, selectedTemplate } = useShipmentFormState()
  const { setWorkflow, applyLoadTemplate, clearLoadTemplate } =
    useShipmentFormActions()
  const unifiedFlow = useUnifiedShipmentFlow()
  const { canViewRates, canBook } = usePermissions()
  const role = useRole()
  const createLoad = useCreateShipment()
  const navigate = useFvNavigate()
  const ratesPage = useLoadRatesPage()
  const scrollRef = useRef<HTMLDivElement>(null)
  const formRef = useRef<ShipmentFormHandle>(null)

  const handleCreate = async (model: ShipmentFormModel) => {
    const result = await createLoad.mutateAsync(
      mapShipmentFormModelToLoadDTO(model, workflow),
    )
    // Deliberately role-based, not `useUnifiedShipmentFlow`: vendors hand
    // truckload shipments off to the shipper, while waterfall accounts quote
    // their own and continue straight to the rates page.
    if (workflow === 'truckload' && role === 'vendor') {
      toast.success('Shipment created', { duration: 5000 })
      scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
      formRef.current?.reset()
      return
    }
    if (!canBook && !canViewRates) {
      return navigate('/pending')
    }
    navigate(ratesPage(result[0]))
  }

  const handleWorkflowChange = (next: Workflow) => {
    if (unifiedFlow.isEnabled(next)) return setWorkflow(next)
    navigate(routes.quote(next))
  }

  return (
    <ShipmentFormPageLayout
      ref={scrollRef}
      sidebarContent={
        <WorkflowSelector value={workflow} onChange={handleWorkflowChange}>
          {!isCopy && role !== 'vendor' && (
            <ShipmentTemplateSelector
              selectedTemplate={selectedTemplate}
              workflow={workflow}
              onSelect={applyLoadTemplate}
              onClear={clearLoadTemplate}
            />
          )}
        </WorkflowSelector>
      }
    >
      <ShipmentForm
        ref={formRef}
        isSaving={createLoad.isPending}
        canSkipItems={workflow === 'truckload'}
        skipInitialAutofill={isCopy}
        onCreate={handleCreate}
      />
    </ShipmentFormPageLayout>
  )
}
